import { Swiper, SwiperSlide } from "swiper/react";
import { Swiper as SwiperClass } from "swiper";
import { EffectCoverflow, Pagination, Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/effect-coverflow";
import "swiper/css/pagination";
import "swiper/css/navigation";
import {
  IOnBoardResult,
  NextVideos,
  Slide,
} from "interfaces/OnbaordingInterface";
import VideoStreaming from "components/video-streaming-onboard/video-streaming.component";
import { useEffect, useRef, useState } from "react";
import Button from "components/button/button";

interface ICarouselProps {
  items: IOnBoardResult[];
  currentSlide: number;
  onBuffer: (id: string) => void;
  onPaused: (id: string) => void;
  onError: (id: string) => void;
  onStart: (id: string) => void;
  onProgress: (data: any, id: string) => void;
  onEnd: (id: string) => void;
  onSeek?: (id: string) => void;
  enableNext?: boolean;
  handleSortNow?: () => void;
  setMobileIndex: any;
  count: number;
  enableFinishButton: boolean;
  setCurrentSlide: (currentSlide: number) => void;
}

const HorizontolCarousel = ({
  items,
  currentSlide,
  onBuffer,
  onPaused,
  onError,
  onStart,
  onProgress,
  onEnd,
  onSeek,
  enableNext,
  handleSortNow,
  setMobileIndex,
  count,
  enableFinishButton,
  setCurrentSlide,
}: ICarouselProps) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [completeSlides, setCompleteSlides] = useState<Slide[]>([]);

  const swiperRef = useRef<SwiperClass | null>(null);

  useEffect(() => {
    // Sort by unlock_order so the carousel position of a given career is
    // stable as new pages load — without this, freshly-appended items can
    // visually shift earlier slides because Swiper keys off DOM position.
    const sortedItems = [...items].sort(
      (a, b) => (a.unlock_order ?? 0) - (b.unlock_order ?? 0)
    );
    const allSlides: Slide[] = sortedItems.map((item, index) => ({
      type: "main" as const,
      data: item,
      index,
    }));
    // Add blurred teaser slides for upcoming videos
    // Use next_videos from the item with the highest unlock_order (most recently unlocked)
    // to ensure teasers show the correct next careers in sequence
    const loadedIds = new Set(sortedItems.map((item) => item.id));
    const latestItem = sortedItems[sortedItems.length - 1];
    const nextVideos = (latestItem?.next_videos ?? []).filter(
      (nv: any) => !loadedIds.has(nv.id)
    );
    const remainingCount = Math.max(0, count - sortedItems.length);
    const teaserSlots = Math.min(remainingCount, 2);
    const nextToShow = nextVideos.slice(0, teaserSlots);
    for (const nv of nextToShow) {
      allSlides.push({
        type: "next_video" as const,
        data: nv,
        index: allSlides.length,
      });
    }
    // Backfill with placeholders if we have fewer next_videos than teaser slots
    for (let i = nextToShow.length; i < teaserSlots; i++) {
      allSlides.push({
        type: "next_video" as const,
        data: null as any,
        index: allSlides.length,
      });
    }
    setCompleteSlides(allSlides);
  }, [items, count]);

  return (
    <div className="w-full mx-auto h-auto pt-4 pb-2">
      <Swiper
        grabCursor={true}
        centeredSlides={true}
        slidesPerView="auto"
        effect={"cards"}
        loop={false}
        initialSlide={0}
        spaceBetween={200}
        allowTouchMove={false}
        coverflowEffect={{
          rotate: 0,
          stretch: 0,
          depth: 200,
          modifier: 1,
          slideShadows: false,
        }}
        modules={[EffectCoverflow, Navigation]}
        breakpoints={{
          560: { slidesPerView: 2 },
          768: { slidesPerView: 2.5 },
          1024: { slidesPerView: 3 },
        }}
        navigation={false}
        onSlideChange={(swiper: SwiperClass) => {
          setActiveIndex(swiper.activeIndex);
          setCurrentIndex(swiperRef.current?.realIndex as number);
          setMobileIndex(swiperRef.current?.realIndex);
        }}
        onSwiper={(swiper: SwiperClass) => (swiperRef.current = swiper)}
      >
        {completeSlides.map((slide, idx) => (
          <SwiperSlide
            key={
              slide.type === "main"
                ? `main-${(slide.data as IOnBoardResult).id}`
                : slide.data
                ? `teaser-${(slide.data as NextVideos).id}`
                : `placeholder-${idx}`
            }
            className={`relative transition-all duration-300 ease-in-out ${
              activeIndex !== idx
                ? "filter blur-sm pointer-events-none"
                : ""
            }`}
          >
            <div className="w-max flex flex-col justify-center">
              {slide.type === "main" ? (
                <VideoStreaming
                  videoRef={true}
                  isPaused={true}
                  videoStatus={
                    (slide.data as IOnBoardResult).cover_video
                      ?.deeper_dive_status
                  }
                  currentIndex={slide.index!}
                  currentSlide={currentSlide}
                  enableButtons={false}
                  header={{
                    title: (slide.data as IOnBoardResult).career.name,
                    subtitle: (slide.data as IOnBoardResult).career.superpower,
                    children: (
                      <span className="flex w-[276px] max-w-[276px] text-[15px] leading-[20px]">
                        {(slide.data as IOnBoardResult).career.description}
                      </span>
                    ),
                    image: (slide.data as IOnBoardResult).career.icon_url,
                  }}
                  video_url={
                    (slide.data as IOnBoardResult).cover_video.expert_voice
                      .voice_url
                  }
                  thumbnail={
                    (slide.data as IOnBoardResult).cover_video.expert_voice
                      .voice_thumbnail
                  }
                  key={idx}
                  onBuffer={() =>
                    onBuffer((slide.data as IOnBoardResult).cover_video.id)
                  }
                  onStart={() =>
                    onStart((slide.data as IOnBoardResult).cover_video.id)
                  }
                  onProgress={(data) =>
                    onProgress(
                      data,
                      (slide.data as IOnBoardResult).cover_video.id
                    )
                  }
                  onPaused={() =>
                    onPaused((slide.data as IOnBoardResult).cover_video.id)
                  }
                  onEnd={() =>
                    onEnd((slide.data as IOnBoardResult).cover_video.id)
                  }
                  onError={() =>
                    onError((slide.data as IOnBoardResult).cover_video.id)
                  }
                  onSeek={onSeek}
                  enableNext={enableNext}
                  handleSortNow={handleSortNow}
                  className="!w-[276px] !h-[487px]"
                  popupClass="relative sm:grid sm:grid-cols-2"
                  count={count}
                  activeIndex={activeIndex}
                  index={slide.index!}
                  description={
                    (slide.data as IOnBoardResult).career.description
                  }
                  image={(slide.data as IOnBoardResult).career.icon_url}
                  title={(slide.data as IOnBoardResult).career.name}
                  subtitle={(slide.data as IOnBoardResult).career.superpower}
                  handleSlideBack={() => {
                    swiperRef.current?.slidePrev();
                  }}
                  handleSlideNext={() => {
                    swiperRef.current?.slideNext();
                  }}
                  leftButton={activeIndex === idx && idx > 0 ? (
                    <Button
                      variant="icon"
                      className="shadow-md"
                      aria-label="Previous video"
                      onClick={() => {
                        swiperRef.current?.slidePrev();
                        setCurrentSlide(currentSlide - 1);
                      }}
                    >
                      <svg aria-hidden="true" width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M13 8H3M3 8L7 4M3 8L7 12" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </Button>
                  ) : undefined}
                  rightButton={activeIndex === idx && idx < count - 1 ? (
                    <Button
                      variant="icon"
                      className="shadow-md"
                      aria-label="Next video"
                      onClick={() => {
                        swiperRef.current?.slideNext();
                        setCurrentSlide(currentSlide + 1);
                      }}
                      disabled={!enableNext}
                    >
                      <svg aria-hidden="true" width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M3 8H13M13 8L9 4M13 8L9 12" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </Button>
                  ) : undefined}
                />
              ) : (
                <div className="w-max flex flex-col justify-center">
                  <div className="h-[160px] mb-3" />
                  <div className="rounded-xl w-[276px] h-[430px] overflow-hidden">
                    {slide.data && (slide.data as NextVideos).voice_thumbnail ? (
                      <img
                        src={(slide.data as NextVideos).voice_thumbnail}
                        alt="Next Video"
                        className="rounded-xl h-[430px] w-[276px] object-cover"
                      />
                    ) : (
                      <div className="rounded-xl w-[276px] h-[430px] bg-brand/20 flex items-center justify-center">
                        <div className="w-[80px] h-[80px] rounded-full bg-brand/30" />
                      </div>
                    )}
                  </div>
                </div>
              )}

              {activeIndex === idx && (
                <div className="flex justify-center items-center gap-1 mt-6 pointer-events-none w-[276px]">
                  {Array.from(Array(count)).map((_, index) => (
                    <div
                      key={index}
                      className={`h-1.5 rounded-full transition-all duration-300 ${
                        currentIndex === index ? "w-4 bg-action" : "w-1.5 bg-border"
                      }`}
                    />
                  ))}
                </div>
              )}
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
};

export default HorizontolCarousel;
